/**
 * Area composition: Census housing tenure, age and households, ethnic groups
 * and religion for the matched small area, with every count and source
 * version. Descriptive shares only; nothing here is a rating.
 */
import type { CSSProperties, ReactNode } from 'react'
import { IconUsersGroup } from '@tabler/icons-react'
import type { AreaPotential, CompositionCategory, CompositionSection } from '../../../api/areaPotential'
import { safeExternalHttpUrl } from '../../../api/diligence'
import { date, label as titleCase, num } from './format'
import { Help, Note, Panel } from './ui'

const percent = (value: number | null | undefined) => value == null ? 'Unknown' : `${num(value)}%`
/** Category colours: tokens only, cycled. */
const PALETTE = ['--sky-400', '--cobalt-400', '--teal-400', '--brick-400', '--emerald-400', '--sky-600', '--slate-400', '--amber-400', '--rose-300']
const swatch = (index: number): CSSProperties => ({ '--c': `var(${PALETTE[index % PALETTE.length]})` } as CSSProperties)
const width = (value: number | null) => `${Math.max(0, Math.min(100, value ?? 0))}%`
const ageLabel = (value: string) => value.replace(/^Aged (\d+) to (\d+) years$/, '$1–$2').replace(/^Aged (\d+) years and over$/, '$1+')

function SourceNote({ section }: { section: CompositionSection }) {
  const source = safeExternalHttpUrl(section.sourceUrl)
  const match = section.geographyMatchMethod === 'uprn_exact' ? 'Exact UPRN match' : section.geographyMatchMethod === 'postcode_modal_straddling' ? 'Postcode approximation · crosses area boundaries' : section.geographyMatchMethod === 'postcode_modal' ? 'Postcode approximation' : titleCase(section.geographyMatchMethod)
  const census = `Census ${section.censusDate?.slice(0, 4) ?? 'Unknown'}`
  return <p className="dil-source">{source ? <a href={source} target="_blank" rel="noopener noreferrer">{census}</a> : census} · {date(section.censusDate)} · {section.lsoaName || section.lsoaCode}<br />{section.lsoaCode} · {match}</p>
}

function Categories({ categories, counts = false, tracks = true }: { categories: CompositionCategory[]; counts?: boolean; tracks?: boolean }) {
  return (
    <ul className={tracks ? 'dil-cats' : 'dil-cats is-legend'}>
      {categories.map((category, index) => (
        <li key={category.code} style={swatch(index)}>
          <div className="dil-cat-head"><span><i aria-hidden="true" />{category.label}</span><span className="dil-num">{percent(category.percent)}{counts ? <small> · {num(category.count)}</small> : null}</span></div>
          {tracks && category.percent != null ? <span className="dil-track is-cat" aria-hidden="true"><i style={{ width: width(category.percent) }} /></span> : null}
        </li>
      ))}
    </ul>
  )
}

function Section({ title, section, children, note }: { title: string; section: CompositionSection | null | undefined; children: ReactNode; note?: string }) {
  if (!section) return null
  return (
    <section className="dil-comp" aria-label={title}>
      <header><h4>{title}</h4><span>{section.denominatorUnit ? `% of ${section.denominatorUnit}` : 'Denominator unknown'}</span></header>
      {children}
      {note ? <Note>{note}</Note> : null}
      <p className="dil-source">{num(section.denominator)} {section.denominatorUnit} in this Census area</p>
      <SourceNote section={section} />
    </section>
  )
}

export function AreaComposition({ response }: { response: AreaPotential }) {
  const composition = response.diligence?.areaComposition
  const demographics = composition?.demographics
  const tenure = composition?.tenure
  const ethnicity = composition?.ethnicity
  const religion = composition?.religion
  const ages = demographics?.categories ?? []
  const ageScale = Math.max(10, Math.ceil(Math.max(0, ...ages.map(category => category.percent ?? 0)) / 10) * 10)
  const ageRows = { '--rows': String(Math.max(1, Math.ceil(ages.length / 2))) } as CSSProperties
  const broad = ethnicity?.categories?.filter(category => category.isAggregate) ?? []
  const groups = broad.length ? broad : ethnicity?.categories ?? []
  if (!tenure && !demographics && !ethnicity && !religion) return null
  return (
    <Panel wide title="Area composition" icon={<IconUsersGroup className="ic" aria-hidden="true" />} className="dil-composition"
      rating={<Help label="Area composition" help={{ what: 'Census housing tenure, age, household composition, ethnic groups and religious affiliation for the matched small area.', why: 'Helps understand the local housing mix and household needs alongside market evidence.', read: 'These are descriptive shares from 0% to 100%, not ratings: a larger share is not inherently better or worse. Compare the mix with the housing need you want to understand; there is no universal ideal mix. Household and resident percentages have different denominators. Ethnicity and religion do not change investment scores. Missing evidence stays unknown. Totals can differ slightly because of published statistical disclosure controls and rounding.' }} />}>
      <Note>Housing, households and the people who live in this area.</Note>
      <div className="dil-cols">
        <Section title="Housing tenure" section={tenure}>
          {tenure?.categories?.some(category => category.percent != null) ? <div className="dil-stack" aria-hidden="true">{tenure.categories.map((category, index) => <span key={category.code} style={{ ...swatch(index), width: width(category.percent) }} />)}</div> : null}
          <Categories categories={tenure?.categories ?? []} tracks={false} />
        </Section>
        <Section title="Age & households" section={demographics}>
          {demographics ? <>
            <div className="dil-ages" style={ageRows} aria-label={`Age distribution, percentage of ${demographics.denominatorUnit ?? 'unknown denominator'}; each track spans 0 to ${num(ageScale)} percent`}>
              {ages.map(category => (
                <div key={category.code}>
                  <span className="dil-age-label" aria-label={category.label}>{ageLabel(category.label)}</span>
                  {category.percent != null ? <span className="dil-track is-cat" aria-hidden="true"><i style={{ width: width(category.percent / ageScale * 100) }} /></span> : <span />}
                  <span className="dil-num">{percent(category.percent)}</span>
                </div>
              ))}
            </div>
            {ages.some(category => category.percent != null) ? <p className="dil-source">Each track spans 0–{num(ageScale)}% of {demographics.denominatorUnit ?? 'unknown denominator'}.</p> : null}
            <div className="dil-highlight"><span>Households with dependent children</span><span className="dil-num">{percent(demographics.householdsWithChildrenPercent)}</span><small>{num(demographics.householdsWithChildren)} of {num(demographics.households)} households</small></div>
          </> : null}
        </Section>
        <Section title="Ethnic groups" section={ethnicity} note="Self-reported ethnic groups · descriptive context">
          <Categories categories={groups} />
          {broad.length ? <details className="dil-more"><summary>Detailed groups &amp; counts</summary>{broad.map(group => <section key={group.code}><h5>{group.label}</h5><Categories categories={ethnicity?.categories?.filter(category => category.parentCode === group.code && !category.isAggregate) ?? []} counts /></section>)}</details> : null}
        </Section>
        <Section title="Religion" section={religion} note="Voluntary Census question · includes no religion and not answered">
          <Categories categories={religion?.categories ?? []} />
        </Section>
      </div>
      {Object.values(composition ?? {}).some(Boolean) ? (
        <details className="dil-more"><summary>All Census counts and source versions</summary>
          <div className="dil-count-tables">{Object.entries(composition ?? {}).map(([key, section]) => section ? (
            <section key={key}>
              <h5>{titleCase(key)} · {(section as CompositionSection).sourceVersion}</h5>
              <table className="tbl dil-table"><thead><tr><th scope="col">Category</th><th scope="col" className="num">Count</th><th scope="col" className="num">% of {(section as CompositionSection).denominatorUnit}</th></tr></thead>
                <tbody>{(section as CompositionSection).categories?.map(category => <tr key={category.code}><th scope="row">{category.label}</th><td className="num">{num(category.count)}</td><td className="num">{percent(category.percent)}</td></tr>)}</tbody></table>
            </section>
          ) : null)}</div>
        </details>
      ) : null}
    </Panel>
  )
}
